import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import type { RecommendedAgentsResponse } from '../data-contract/recommended-agents';
import type { AgentLibraryScope } from '../fetchers/agents/recommended-agents-fetchers';
import { useOptionalTileAvailability, useOptionalUrlTiles } from '../url-tiles';
import { useUiLocale } from './api/use-recommended-agents';
import {
    descriptionKeyOf,
    type RecommendedAgentRow,
    type RecommendedAgentsSubject,
    shareKeyOf,
    toRecommendedAgentRows,
} from './recommended-agents-model';
import { recommendedAgentsTranslationKeys as keys } from './recommended-agents-translations';
import { AGENT_ATTRIBUTIONS_TILE_TYPE, createAgentAttributionsTilePayload, selectAgentIdFromTile } from './url-tile';

export type RecommendedAgentTaskOpener = (task: { readonly id: string; readonly version: number }) => void;

const LINK_CLASS = 'rounded-xs border-none bg-transparent p-0 text-left hover:underline';
const ROW_CLASS = 'm-0 grid grid-cols-[1.25rem_minmax(0,1fr)_8rem] gap-m border-t border-border-subtle py-l';

const LoadingRows: FunctionComponent = () => {
    const translate = useTranslate();

    return (
        <ol aria-label={translate(keys.loading)} className="m-0 flex list-none flex-col p-0" role="status">
            {[0, 1, 2].map((row) => (
                <li key={row} className={ROW_CLASS}>
                    <SkeletonBase className="h-4 w-4" />
                    <div className="flex flex-col gap-xs">
                        <SkeletonBase className="h-4 w-48" />
                        <SkeletonBase className="h-4 w-full" />
                    </div>
                    <div className="flex flex-col items-end gap-xs">
                        <SkeletonBase className="h-4 w-24" />
                        <SkeletonBase className="h-1 w-full" />
                    </div>
                </li>
            ))}
        </ol>
    );
};

const AgentRow: FunctionComponent<{
    readonly isSelected: boolean;
    readonly onOpen: (() => void) | undefined;
    readonly onOpenTask: RecommendedAgentTaskOpener | undefined;
    readonly position: number;
    readonly row: RecommendedAgentRow;
    readonly subject: RecommendedAgentsSubject;
}> = ({ isSelected, onOpen, onOpenTask, position, row, subject }) => {
    const translate = useTranslate();
    const meta = [
        row.businessFunction,
        row.autonomyLevel === undefined ? undefined : translate(keys.autonomy, { value: row.autonomyLevel }),
    ].filter((part) => part !== undefined);

    return (
        <li className={`${ROW_CLASS} ${isSelected ? 'bg-surface-hover' : ''}`} data-testid="recommended-agent">
            <span className="text-body-s tabular-nums text-text-muted">{position}</span>
            <div className="flex min-w-0 flex-col gap-xs">
                <div className="flex flex-wrap items-center gap-xs">
                    {onOpen === undefined ? (
                        <span className="text-body font-medium text-text-primary">{row.title}</span>
                    ) : (
                        <Button
                            aria-current={isSelected}
                            className={`${LINK_CLASS} text-body font-medium text-text-primary`}
                            variant="plain"
                            onClick={onOpen}
                        >
                            {row.title}
                        </Button>
                    )}
                    {row.leverKeys.map((leverKey) => (
                        <Badge key={leverKey} size="sm" variant="gray">
                            {translate(leverKey)}
                        </Badge>
                    ))}
                </div>
                {row.description === undefined ? null : (
                    <p className="m-0 line-clamp-2 text-body-s text-text-secondary">{row.description}</p>
                )}
                {meta.length === 0 ? null : <p className="m-0 text-body-s text-text-muted">{meta.join(' · ')}</p>}
                <div className="flex min-w-0 items-center gap-xs text-body-s text-text-muted">
                    <span className="shrink-0">{translate(keys.recommendedFor)}</span>
                    <Badge size="sm" variant="gray">
                        {row.tasks.length}
                    </Badge>
                    <span className="min-w-0 truncate" title={row.tasks.map((task) => task.title).join('\n')}>
                        {row.tasks.map((task, index) => (
                            <Fragment key={task.id}>
                                {index > 0 ? ' · ' : null}
                                {onOpenTask === undefined || task.version <= 0 ? (
                                    task.title
                                ) : (
                                    <Button
                                        className={`${LINK_CLASS} text-inherit`}
                                        variant="plain"
                                        onClick={() => onOpenTask({ id: task.id, version: task.version })}
                                    >
                                        {task.title}
                                    </Button>
                                )}
                            </Fragment>
                        ))}
                    </span>
                </div>
            </div>
            <div className="flex flex-col items-end gap-xxs text-right">
                {row.share === undefined ? null : (
                    <span className="text-body font-medium tabular-nums text-text-primary">
                        {translate(shareKeyOf(subject), { value: row.share })}
                    </span>
                )}
                <span className="text-body-s tabular-nums text-text-muted">
                    {translate(keys.hoursPerWeek, { value: row.hoursPerWeek })}
                </span>
                <div aria-hidden={true} className="mt-xs h-1 w-full overflow-hidden rounded-full bg-surface-hover">
                    <div className="h-full rounded-full bg-accent" style={{ width: `${row.barRatio * 100}%` }} />
                </div>
            </div>
        </li>
    );
};

/** The agents recommended for the tasks of one job role, job level or position, most weekly hours freed first. */
export type RecommendedAgentsState = {
    readonly data: RecommendedAgentsResponse | undefined;
    readonly isError: boolean;
    readonly isPending: boolean;
};

export const RecommendedAgentsList: FunctionComponent<{
    /** Opens a task the agent is recommended for; its titles stay plain text without one. */
    readonly onOpenTask: RecommendedAgentTaskOpener | undefined;
    readonly query: RecommendedAgentsState;
    readonly scope: AgentLibraryScope;
    readonly subject: RecommendedAgentsSubject;
}> = ({ onOpenTask, query, scope, subject }) => {
    const translate = useTranslate();
    const locale = useUiLocale();
    const urlTiles = useOptionalUrlTiles();
    const tileAvailability = useOptionalTileAvailability();
    const openAgent =
        urlTiles !== null &&
        (tileAvailability === null || tileAvailability.availabilityByType.has(AGENT_ATTRIBUTIONS_TILE_TYPE))
            ? (agentId: string) => urlTiles.openTile(createAgentAttributionsTilePayload(scope, agentId))
            : undefined;
    const openAgentId = urlTiles?.tiles.map(({ payload }) => selectAgentIdFromTile(payload, scope)).find(is.string);
    const rows = useMemo(
        () => (query.data === undefined ? [] : toRecommendedAgentRows(query.data, locale)),
        [query.data, locale],
    );

    return (
        <section className="mt-xxl flex flex-col gap-3xl" data-testid="recommended-agents">
            <header className="flex flex-col gap-xs">
                <h3 className="m-0 mb-3xl text-body-lg font-medium tracking-tight text-text-primary">
                    {translate(keys.title)}
                </h3>
                <p className="m-0 text-body-s text-text-secondary">{translate(descriptionKeyOf(subject))}</p>
                {query.data !== undefined && query.data.recommendedTaskCount < query.data.taskCount ? (
                    <p className="m-0 text-body-s text-text-muted">
                        {translate(keys.coverage, {
                            recommended: query.data.recommendedTaskCount,
                            total: query.data.taskCount,
                        })}
                    </p>
                ) : null}
            </header>
            {query.isPending ? (
                <LoadingRows />
            ) : query.isError ? (
                <p className="m-0 text-body-s text-text-secondary" role="alert">
                    {translate(keys.error)}
                </p>
            ) : rows.length === 0 ? (
                <div className="flex flex-col gap-xxs" data-testid="recommended-agents-empty">
                    <p className="m-0 text-body font-medium text-text-primary">{translate(keys.empty)}</p>
                    <p className="m-0 text-body-s text-text-secondary">{translate(keys.emptyDescription)}</p>
                </div>
            ) : (
                <ol className="m-0 flex list-none flex-col p-0">
                    {rows.map((row, index) => (
                        <AgentRow
                            key={row.agentId}
                            isSelected={row.agentId === openAgentId}
                            position={index + 1}
                            row={row}
                            subject={subject}
                            onOpen={openAgent === undefined ? undefined : () => openAgent(row.agentId)}
                            onOpenTask={onOpenTask}
                        />
                    ))}
                </ol>
            )}
        </section>
    );
};
